import { useCallback, useEffect, useMemo, useState } from "react";
import Segmented from "@/components/Segmented";
import { Loader2, AlertTriangle, PlugZap, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { opsApi, money } from "./lib/api";
import BillingReviewDrawer from "./BillingReviewDrawer";

const CHIPS = [
  ["all", "Every job", (r) => true],
  ["awaiting_pm", "Awaiting PM", (r) => r.status === "review_required"],
  ["not_ready", "Not ready to bill", (r) => r.not_ready],
  ["pm_answered", "PM answered", (r) => ["pm_approved", "pm_modified", "accountant_review"].includes(r.status)],
  ["accountant_approved", "Approved", (r) => r.status === "accountant_approved"],
  ["needs_adjustment", "Needs adjustment", (r) => r.status === "needs_adjustment"],
  ["no_billing", "No billing", (r) => r.status === "no_billing"],
  ["waiting_info", "Waiting on info", (r) => r.status === "waiting_info"],
  ["past_deadline", "Past deadline", (r) => r.past_deadline],
  ["overrides", "Overrides", (r) => r.override],
  ["submitted", "Submitted", (r) => ["submitted", "paid"].includes(r.status)],
];

const TONE = {
  review_required: "border-amber-200 bg-amber-50 text-amber-800",
  pm_approved: "border-sky-200 bg-sky-50 text-sky-700",
  pm_modified: "border-sky-200 bg-sky-50 text-sky-700",
  accountant_review: "border-sky-200 bg-sky-50 text-sky-700",
  accountant_approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  needs_adjustment: "border-rose-200 bg-rose-50 text-rose-700",
  no_billing: "border-slate-200 bg-slate-50 text-slate-500",
  waiting_info: "border-orange-200 bg-orange-50 text-orange-700",
  submitted: "border-emerald-200 bg-emerald-50 text-emerald-700",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

/** FIN-2 — the billing dashboard. Live, not a monthly report: every billable job, what we
 *  think it should bill, what the PM said and where it sits. Nothing is emailed and nothing
 *  reaches Foundation yet — see the flags below the summary. */
export default function BillingDashboardPage() {
  const [data, setData] = useState(null);
  const [chip, setChip] = useState("all");
  const [open, setOpen] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setData(await opsApi.billingDashboard());
      setLoadedAt(new Date());
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the billing dashboard"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const test = (CHIPS.find(([k]) => k === chip) || CHIPS[0])[2];
    return data.rows.filter(test);
  }, [data, chip]);

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading the billing dashboard…
    </div>;
  }
  const s = data.summary;
  const counts = Object.fromEntries(
    CHIPS.map(([key, , test]) => [key, data.rows.filter(test).length]));

  return (
    <div className="page-body space-y-5" data-testid="billing-dashboard-page">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[["Jobs billing this cycle", `${s.projects}`, "billing-kpi-projects"],
          ["Awaiting a PM answer", `${s.awaiting_pm}`, "billing-kpi-awaiting"],
          ["Past the internal deadline", `${s.past_deadline}`, "billing-kpi-late"],
          ["On the table", money(s.total_billing), "billing-kpi-total"]]
          .map(([label, value, tid]) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white p-4"
                 data-testid={tid}>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
              <p className="stat-num text-2xl font-semibold text-slate-900">{value}</p>
            </div>
          ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="billing-chips">
        <Segmented
          options={CHIPS.map(([key, label]) => ({ key, label, count: counts[key] ?? 0,
                                                  testId: `billing-chip-${key}` }))}
          value={chip}
          onChange={setChip}
        />
        <button onClick={load} disabled={busy} data-testid="billing-refresh"
                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-500 hover:border-slate-400">
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
          {loadedAt ? `Refreshed ${loadedAt.toLocaleTimeString()}` : "Refresh"}
        </button>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
           data-testid="billing-not-wired">
        <p className="flex items-center gap-2 text-xs font-semibold text-amber-900">
          <PlugZap className="h-3.5 w-3.5" /> Not connected yet
        </p>
        <ul className="mt-1.5 space-y-1 text-xs text-amber-900">
          {data.not_wired.map((n, i) => <li key={i}>· {n}</li>)}
        </ul>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white"
           data-testid="billing-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Job</th>
              <th className="px-3 py-2 text-left">PM</th>
              <th className="px-3 py-2 text-left">GC</th>
              <th className="px-3 py-2 text-left">Platform</th>
              <th className="px-3 py-2 text-left">Deadlines</th>
              <th className="px-3 py-2 text-right">Contract</th>
              <th className="px-3 py-2 text-right">Billed to date</th>
              <th className="px-3 py-2 text-right">Suggested</th>
              <th className="px-3 py-2 text-right">This billing</th>
              <th className="px-3 py-2 text-right">Ret.</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.project_id} onClick={() => setOpen(r)}
                  data-testid={`billing-row-${r.job_number || r.project_id}`}
                  className={`cursor-pointer hover:bg-slate-50 ${
                    r.past_deadline ? "bg-rose-50/60" : ""}`}>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs text-slate-500">{r.job_number || "—"}</span>
                  <p className="truncate font-medium text-slate-900">{r.name}</p>
                  {r.not_ready && (
                    <p className="mt-0.5 flex items-start gap-1 text-[11px] font-medium text-rose-700"
                       data-testid={`billing-blockers-${r.job_number || r.project_id}`}>
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      <span>{r.blockers.join(" · ")}</span>
                    </p>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {(r.pm_email || "").split("@")[0]
                    || <span className="text-rose-600">no PM assigned</span>}
                </td>
                <td className="max-w-[10rem] truncate px-3 py-2 text-xs text-slate-600">
                  {r.gc || "—"}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.platform || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <p className={r.past_deadline ? "font-semibold text-rose-700" : "text-slate-700"}>
                    Internal {r.internal_deadline}
                    {r.past_deadline && <AlertTriangle className="ml-1 inline h-3 w-3" />}
                  </p>
                  <p className="text-slate-400">GC {r.gc_due}</p>
                </td>
                <td className="px-3 py-2 text-right text-xs">{money(r.current_contract)}</td>
                <td className="px-3 py-2 text-right text-xs">{money(r.previously_billed)}</td>
                <td className="px-3 py-2 text-right text-xs">
                  {r.not_ready ? <span className="text-rose-600">not ready</span> : (
                    <>
                      {money(r.suggested)}
                      {r.suggested_is_placeholder && (
                        <span title="No engine suggestion for this period"
                              className="ml-1 text-amber-600">*</span>
                      )}
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-xs font-semibold">
                  {money(r.current_billing)}
                  {r.override && (
                    <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800">
                      {r.override_delta > 0 ? "+" : ""}{Math.round(r.override_delta).toLocaleString()}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-xs text-slate-500">
                  {r.retention_percent ? `${r.retention_percent}%` : "—"}
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] ${
                    TONE[r.status] || "border-slate-200 bg-slate-50 text-slate-600"}`}>
                    {r.status_label}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-sm text-slate-500">
                Nothing here. Jobs appear once they are Ready or Open with billing required.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data.suggested_placeholder && (
        <p className="text-xs text-slate-500">
          * These jobs have no engine suggestion for this period — either the job is not ready to
          bill, or the record was frozen before the suggested billing engine went in.
        </p>
      )}

      {open && (
        <BillingReviewDrawer row={open} onClose={() => setOpen(null)}
                             onSaved={load} />
      )}
    </div>
  );
}
